import { defineStore } from "pinia";
import { syncThemeChrome } from "../helpers/theme-chrome.js";

export const useUiStore = defineStore("ui", {
  state: () => ({
    tvMode: false,
    tvAutoDetected: false,
    // The captured `beforeinstallprompt` BeforeInstallPromptEvent. Held in
    // module/store memory only — not persisted (the event isn't serializable
    // and is single-use per page load anyway).
    _installPromptEvent: null,
    // Mirror flag exposed reactively to components. Set by setInstallPromptAvailable().
    installPromptAvailable: false,
    // Set true while a full-viewport cinematic player is mounted (currently:
    // MediaView's TV-mode VIDEO branch, TorboxPlayView TV branch). App.vue
    // hides the slim header while this is on so the fixed z-50 slim chrome
    // doesn't stack on top of the cinematic z-40 top-bar.
    cinematicPlayerActive: false,
    // True while the AccountMenu avatar dropdown is open. TvSlimHeader reads
    // this to keep the slim chrome pinned visible (so the dropdown doesn't
    // disappear under it after the 3s idle timer). Also lets the mobile
    // hamburger close itself when the avatar menu opens.
    accountMenuOpen: false,
    // True while the mobile hamburger nav dropdown is open. AccountMenu
    // watches it so opening the hamburger collapses the avatar menu (the
    // two used to overlap on small screens when both were open).
    mobileMenuOpen: false,
    // Monotonic counter bumped by `openSearchOverlay()`. App.vue watches it
    // and opens the header search overlay (prefilled from ?q=, caret at end)
    // so any view — e.g. the active "search" chip on the Discover results
    // page — can let the user edit their query without re-typing it. A
    // counter (not a boolean) so repeated requests always re-fire the watcher.
    // Not persisted — it's a transient one-shot signal.
    searchOverlayTick: 0,
    // Cross-view language filter (ISO-639-1 code, '' = All languages).
    // Remembers the language the user picked on /discover or in the
    // library views so it carries across Discover tabs and between
    // Discover ↔ Libraries (the nav links don't preserve `?lang=`).
    // Source of truth is the `?lang=` URL param on the active view;
    // this mirrors it so the choice survives navigation. Deliberately
    // NOT persisted (see `persist.pick`) — it's a transient filter, and
    // the URL already restores it on reload of a language-bearing page.
    languageFilter: "",
  }),
  // Persist only stable user preferences. The install prompt event is volatile
  // and the boolean flag must be re-derived from a fresh `beforeinstallprompt`
  // capture on each page load. NOTE: pinia-plugin-persistedstate v4 renamed
  // `paths` → `pick`; the old name was silently ignored, which persisted the
  // WHOLE store on every mutation (menu toggles, ticks) AND restored
  // intentionally-transient state.
  persist: {
    pick: ["tvMode", "tvAutoDetected"],
  },
  actions: {
    setTvMode(value) {
      const tvMode = Boolean(value);
      this.tvMode = tvMode;
      this.applyTvModeClass();
    },

    /**
     * Re-applies the `.tv-mode` class on `<html>` based on current `tvMode`
     * state. Called by `setTvMode` and by App.vue on first mount so that a
     * persisted `tvMode = true` from localStorage is reflected in the DOM
     * before first paint.
     */
    applyTvModeClass() {
      if (typeof document === "undefined") return;
      const html = document.documentElement;
      if (this.tvMode) {
        html.classList.add("tv-mode");
        // TV mode is dark-only — force the `dark` class so every Tailwind
        // `dark:` utility kicks in regardless of the saved theme preference.
        // The user can still toggle off TV mode and revert to their light
        // preference. We tag the dark class as TV-driven so settingsStore's
        // applyTheme() doesn't fight us.
        html.classList.add("dark");
        html.dataset.tvDarkForced = "1";
        syncThemeChrome();
      } else {
        html.classList.remove("tv-mode");
        // Only un-force dark if WE forced it — otherwise the user's actual
        // theme preference is in effect and shouldn't be touched.
        if (html.dataset.tvDarkForced === "1") {
          delete html.dataset.tvDarkForced;
          // Defer to the settings store to put the dark class back where it
          // should be based on the user's preference.
          try {
            // Lazy import to avoid a circular dep.
            import("@/stores/settings").then(({ useSettingsStore }) => {
              useSettingsStore().applyTheme();
            });
          } catch {
            // If the import fails, leave the dark class on — light flash is
            // worse than the user's preference being briefly ignored.
          }
        }
      }
    },

    setTvAutoDetected(value) {
      this.tvAutoDetected = Boolean(value);
    },

    setCinematicPlayerActive(value) {
      this.cinematicPlayerActive = Boolean(value);
    },

    setAccountMenuOpen(value) {
      this.accountMenuOpen = Boolean(value);
    },

    setMobileMenuOpen(value) {
      this.mobileMenuOpen = Boolean(value);
    },

    // Set the cross-view language filter. Accepts an ISO-639-1 (2-3 letter)
    // code; anything else clears the filter back to "All languages".
    setLanguageFilter(code) {
      this.languageFilter =
        typeof code === "string" && /^[a-z]{2,3}$/.test(code) ? code : "";
    },

    dismissAutoDetect() {
      this.tvAutoDetected = false;
    },

    /**
     * Request that the app-level header search overlay open. App.vue watches
     * `searchOverlayTick` and calls its `openHeaderSearch()` in response. Lets
     * a child view (e.g. DiscoverView's active-search chip) re-open the search
     * input for editing — the overlay prefills from the current `?q=` and puts
     * the caret at the end so a typo can be fixed without retyping.
     */
    openSearchOverlay() {
      this.searchOverlayTick++;
    },

    /**
     * Stash the captured BeforeInstallPromptEvent so the Settings page can
     * fire it later. We keep the actual event off `state` to avoid Pinia's
     * reactive proxy wrapping the native event (which breaks `prompt()` in
     * some browsers); instead it lives on `this._installPromptEvent` as a
     * plain property and we expose a boolean mirror via `installPromptAvailable`.
     */
    setInstallPromptEvent(event) {
      this._installPromptEvent = event || null;
      this.setInstallPromptAvailable(Boolean(event));
    },

    setInstallPromptAvailable(value) {
      this.installPromptAvailable = Boolean(value);
    },

    /**
     * Fire the captured install prompt. Browsers fire `beforeinstallprompt`
     * exactly once per page load, so we consume the event on first call and
     * flip `installPromptAvailable` back to false. Returns the user choice
     * promise (`{ outcome: 'accepted'|'dismissed', platform: string }`) or
     * null if no event was captured.
     */
    async triggerInstallPrompt() {
      const event = this._installPromptEvent;
      if (!event || typeof event.prompt !== "function") {
        this.setInstallPromptAvailable(false);
        return null;
      }
      try {
        event.prompt();
        const choice = event.userChoice ? await event.userChoice : null;
        return choice;
      } finally {
        this._installPromptEvent = null;
        this.setInstallPromptAvailable(false);
      }
    },
  },
});
